import React, { useState, useEffect } from 'react';
import { getData } from '../../services/api.service';
import { toastError, toastSuccess } from '../common/Toast';
import * as XLSX from 'xlsx';
import { FaFileExcel, FaTimes, FaDownload, FaTable } from 'react-icons/fa';
import FormFilterDate from '../common/FormFilterDate';

/**
 * Modal para generar reporte detallado completo de pedidos en Excel
 * Solo accesible para Admin, Finanzas y Taller
 */
function DetailedReportModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [dateFilters, setDateFilters] = useState(null);

  useEffect(() => {
    if (isOpen && !dateFilters) {
      // No cargar automáticamente, esperar que el usuario seleccione fechas
      setOrders([]);
    }
  }, [isOpen]);

  const loadDetailedData = async (filters = null) => {
    try {
      setLoading(true);
      
      let url = '/api/orders/detailed-report';
      
      // Agregar parámetros de fecha si existen
      if (filters?.date_from || filters?.date_to) {
        const params = new URLSearchParams();
        if (filters.date_from) params.append('start_date', filters.date_from);
        if (filters.date_to) params.append('end_date', filters.date_to);
        url += `?${params.toString()}`;
      }
      
      const response = await getData(url, true);
      
      if (response?.data?.orders) {
        setOrders(response.data.orders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Error cargando datos detallados:', error);
      toastError('Error al cargar los datos del reporte');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = (data) => {
    setDateFilters(data);
    loadDetailedData(data);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${parseFloat(amount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
  };

  const exportToExcel = () => {
    try {
      const excelData = orders.map(order => ({
        'PRODUCTOS/DESCRIPCIONES': order.products || '',
        'Tipo Producto (category sku)': order.product_categories || '',
        'ESTADO PEDIDO': order.status_description || '',
        'CÓDIGO': order.order_id || '',
        'NOMBRE y D.N.I': `${order.customer_name || ''} - ${order.customer_dni || ''}`,
        'TELÉFONO': order.customer_phone || '',
        'MAIL': order.customer_email || '',
        'DIRECCIÓN': order.customer_address || '',
        'FECHA COMPRA': formatDate(order.inserted_date),
        'FECHA ENTREGA': formatDate(order.estimated_delivery_date),
        'PRECIO VENTA (Total)': parseFloat(order.total || 0),
        'Acreditado en caja': parseFloat(order.credited_payment || 0),
        'Pagado p/cliente': parseFloat(order.credit_balance || 0),
        'Forma de pago': order.payment_form_description || '',
        'Medio de pago': order.payment_method_description || '',
        'TRANSPORTE': order.delivery_type_description || '',
        'COMENTARIOS': order.observations || '',
        '% Comisión': order.commission_rate ? `${order.commission_rate}%` : '',
        'Vendedor': order.seller_name || ''
      }));

      // Crear workbook y worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Ajustar ancho de columnas
      ws['!cols'] = [
        { wch: 40 },  // PRODUCTOS/DESCRIPCIONES
        { wch: 30 },  // Tipo Producto
        { wch: 18 },  // ESTADO PEDIDO
        { wch: 10 },  // CÓDIGO
        { wch: 35 },  // NOMBRE y D.N.I
        { wch: 15 },  // TELÉFONO
        { wch: 25 },  // MAIL
        { wch: 40 },  // DIRECCIÓN
        { wch: 15 },  // FECHA COMPRA
        { wch: 15 },  // FECHA ENTREGA
        { wch: 15 },  // PRECIO VENTA
        { wch: 18 },  // Acreditado en caja
        { wch: 18 },  // Pagado p/cliente
        { wch: 20 },  // Forma de pago
        { wch: 20 },  // Medio de pago
        { wch: 20 },  // TRANSPORTE
        { wch: 40 },  // COMENTARIOS
        { wch: 12 },  // % Comisión
        { wch: 25 }   // Vendedor
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Reporte Detallado');

      // Generar archivo
      const fileName = `Reporte_Detallado_Pedidos_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      toastSuccess('Archivo Excel generado exitosamente');
    } catch (error) {
      console.error('Error generando Excel:', error);
      toastError('Error al generar el archivo Excel');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <FaFileExcel className="me-2 text-info" />
              Reporte Detallado de Pedidos
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>

          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Filtro de fechas */}
            <div className="mb-3">
              <FormFilterDate handleInfo={handleDateFilter} />
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <div className="mt-2">Cargando pedidos...</div>
              </div>
            ) : orders.length === 0 && !dateFilters ? (
              <div className="alert alert-info">
                <strong>ℹ️ Información:</strong> Seleccione un rango de fechas para cargar los pedidos.
              </div>
            ) : orders.length === 0 ? (
              <div className="alert alert-info">
                <FaTable className="me-2" />
                No hay pedidos para mostrar en el rango de fechas seleccionado
              </div>
            ) : (
              <>
                <div className="alert alert-success mb-3">
                  <strong>{orders.length}</strong> pedido(s) listos para exportar
                </div>

                <div className="table-responsive" style={{ maxHeight: '50vh' }}>
                  <table className="table table-sm table-bordered table-hover">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Código</th>
                        <th>Cliente</th>
                        <th>Estado</th>
                        <th>Total</th>
                        <th>Vendedor</th>
                        <th>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.order_id}>
                          <td>#{order.order_id}</td>
                          <td>{order.customer_name}</td>
                          <td>
                            <span className="badge bg-secondary">
                              {order.status_description}
                            </span>
                          </td>
                          <td>{formatCurrency(order.total)}</td>
                          <td>{order.seller_name}</td>
                          <td>{formatDate(order.inserted_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              <FaTimes className="me-2" />
              Cerrar
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={exportToExcel}
              disabled={loading || orders.length === 0}
            >
              <FaDownload className="me-2" />
              Descargar Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DetailedReportModal;
